import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api, type CryptoJournal, type CryptoJournalClosedCycle, type CryptoJournalOpenCycle } from '@/api/client'
import { formatTime } from '@/lib/utils'
import { symbolName } from '@/lib/krx-names'
import {
  Badge,
  Card,
  EmptyState,
  StatCard,
  Table,
} from '@/admin-design/_common'

// Journal page — operator's "사고-팔 때 대비 수익률" view of both
// the crypto bot (Bithumb) and KIS (한국주식) activity. Tabs
// switch between the two ledgers; shape is identical so all section
// components are reused.
//
//   1. 일일 합계   StatCards: today's realised KRW, W/L, trade count.
//   2. 보유 중     open positions (crypto: live mark + unrealised).
//   3. 마감된 거래 round-trip cycles newest-first with entry/exit avg
//                  and PnL%.
//
// Decimal-string values arrive from the server; the UI parses to
// number only for display rounding so crypto precision is preserved.

type Tab = 'crypto' | 'kis'

const fmtKRW = (s: string | undefined) => {
  if (!s || s === '') return '—'
  const n = Number(s)
  if (!Number.isFinite(n)) return s
  return new Intl.NumberFormat('ko-KR').format(Math.round(n)) + ' ₩'
}
const fmtSignedKRW = (s: string | undefined) => {
  if (!s || s === '') return '—'
  const n = Number(s)
  if (!Number.isFinite(n)) return s
  const sign = n > 0 ? '+' : ''
  return sign + new Intl.NumberFormat('ko-KR').format(Math.round(n)) + ' ₩'
}
const fmtPct = (n: number | undefined) => {
  if (n == null || !Number.isFinite(n)) return '—'
  const sign = n > 0 ? '+' : ''
  return sign + n.toFixed(2) + '%'
}
const fmtVol = (s: string | undefined) => {
  if (!s) return '—'
  if (s.includes('.')) return s.replace(/\.?0+$/, '')
  return s
}
const pnlClass = (n: number | undefined) =>
  n == null || n === 0
    ? 'text-muted-foreground'
    : n > 0
    ? 'text-green-700 dark:text-green-400'
    : 'text-red-700 dark:text-red-400'

export default function JournalPage() {
  const [tab, setTab] = useState<Tab>('crypto')
  return (
    <div className="space-y-6 p-4 sm:p-6">
      <header className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-foreground">
            Journal
          </h1>
          <p className="text-xs text-muted-foreground">
            사고-팔 때 대비 수익률. 30초마다 갱신.
          </p>
        </div>
        <Tabs tab={tab} onChange={setTab} />
      </header>

      {tab === 'crypto' ? <CryptoView /> : <KISView />}
    </div>
  )
}

function Tabs({ tab, onChange }: { tab: Tab; onChange: (t: Tab) => void }) {
  const base =
    'px-3 py-1.5 text-sm font-medium rounded-md transition-colors'
  return (
    <div className="inline-flex gap-1 rounded-lg border border-border bg-card p-1">
      <button
        type="button"
        onClick={() => onChange('crypto')}
        className={`${base} ${
          tab === 'crypto'
            ? 'bg-primary text-primary-foreground'
            : 'text-muted-foreground hover:bg-muted'
        }`}
      >
        Crypto
      </button>
      <button
        type="button"
        onClick={() => onChange('kis')}
        className={`${base} ${
          tab === 'kis'
            ? 'bg-primary text-primary-foreground'
            : 'text-muted-foreground hover:bg-muted'
        }`}
      >
        주식
      </button>
    </div>
  )
}

function CryptoView() {
  const q = useQuery<CryptoJournal>({
    queryKey: ['crypto-journal'],
    queryFn: () => api.cryptoJournal(),
    refetchInterval: 30_000,
  })
  return (
    <JournalSections
      data={q.data}
      isPending={q.isPending}
      kind="crypto"
      modeBadge={
        q.data && (
          <Badge variant={q.data.trade_mode === 'live' ? 'destructive' : 'muted'}>
            {q.data.trade_mode === 'live' ? 'LIVE 실전' : 'PAPER 모의'}
          </Badge>
        )
      }
    />
  )
}

function KISView() {
  const q = useQuery<CryptoJournal>({
    queryKey: ['kis-journal'],
    queryFn: () => api.kisJournal(),
    refetchInterval: 30_000,
  })
  return (
    <JournalSections
      data={q.data}
      isPending={q.isPending}
      kind="kis"
      modeBadge={
        q.data && (
          <Badge variant={q.data.trade_mode === 'live' ? 'destructive' : 'muted'}>
            {q.data.trade_mode === 'live' ? 'LIVE 실전' : 'PAPER 모의'}
          </Badge>
        )
      }
    />
  )
}

function JournalSections({
  data,
  isPending,
  kind,
  modeBadge,
}: {
  data: CryptoJournal | undefined
  isPending: boolean
  kind: 'crypto' | 'kis'
  modeBadge: React.ReactNode
}) {
  const showPaperHint =
    data?.trade_mode === 'live' && data?.paper_exists === true

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-end gap-3">
        {showPaperHint && (
          <span className="text-xs text-muted-foreground">
            PAPER 기록도 있음 (모드 전환 시 표시)
          </span>
        )}
        {modeBadge}
      </div>
      <DailySummarySection data={data} isPending={isPending} />
      <OpenCyclesSection
        cycles={data?.open_cycles}
        isPending={isPending}
        kind={kind}
      />
      <ClosedCyclesSection
        cycles={data?.closed_cycles}
        isPending={isPending}
        kind={kind}
      />
    </div>
  )
}

function DailySummarySection({
  data,
  isPending,
}: {
  data: CryptoJournal | undefined
  isPending: boolean
}) {
  const d = data?.daily
  // 실현 손익 = 청산된 라운드트립 손익(청산 횟수/승패와 정합). 예전엔 NCF(순현금흐름)를
  // 보여줘서 "청산 0인데 손익 +"가 떴다 — NCF는 손익이 아니라 현금 이동이다.
  const realisedClosed = d ? Number(d.realised_closed_krw) : undefined
  const ncf = d ? Number(d.realised_krw) : undefined
  const realisedAllTime = d ? Number(d.realised_all_time_krw) : undefined
  return (
    <section>
      <h2 className="text-sm font-semibold text-muted-foreground mb-3">
        오늘 (KST {d?.date ?? '—'})
      </h2>
      <div className="grid gap-3 grid-cols-2 md:grid-cols-4">
        <StatCard
          label="실현 손익 (청산분)"
          value={isPending ? '…' : fmtSignedKRW(d?.realised_closed_krw)}
          tone={realisedClosed == null || realisedClosed === 0 ? 'muted' : realisedClosed > 0 ? 'success' : 'destructive'}
        />
        <StatCard label="청산 횟수" value={isPending ? '…' : String(d?.trades_closed ?? 0)} />
        <StatCard label="이긴 거래" value={isPending ? '…' : String(d?.wins ?? 0)} tone="success" />
        <StatCard label="진 거래" value={isPending ? '…' : String(d?.losses ?? 0)} tone="destructive" />
      </div>
      {!isPending && d && ncf !== undefined && realisedClosed !== undefined && ncf !== realisedClosed && (
        <p className="mt-2 text-[11px] text-muted-foreground">
          현금흐름(순): <span className="font-mono">{fmtSignedKRW(d.realised_krw)}</span> · 오늘 매도−매수(미청산 매수 포함) — <b>손익이 아니라 현금 이동</b>입니다.
        </p>
      )}
      <h2 className="text-sm font-semibold text-muted-foreground mt-5 mb-3">
        전체 누적
      </h2>
      <div className="grid gap-3 grid-cols-2 md:grid-cols-3">
        <StatCard
          label="전체 실현손익"
          value={isPending ? '…' : fmtSignedKRW(d?.realised_all_time_krw)}
          tone={
            realisedAllTime == null || realisedAllTime === 0
              ? 'muted'
              : realisedAllTime > 0
              ? 'success'
              : 'destructive'
          }
        />
        <StatCard
          label="전체 마감"
          value={isPending ? '…' : String(d?.trades_closed_all_time ?? 0)}
        />
        <StatCard
          label="미청산"
          value={isPending ? '…' : String(d?.open_count ?? 0)}
        />
      </div>
    </section>
  )
}

// SymbolCell renders the symbol followed by the Korean stock name
// when the ledger is KIS. Prefers the backend-supplied name (KIS
// broker prdt_name) over the static client-side map so tickers
// outside the bundled KRX_NAMES list still resolve.
function SymbolCell({
  sym,
  name,
  kind,
}: {
  sym: string
  name?: string
  kind: 'crypto' | 'kis'
}) {
  const resolved = kind === 'kis' ? name || symbolName(sym) : ''
  return (
    <>
      <span className="font-mono">{sym}</span>
      {resolved && (
        <span className="ml-1.5 text-xs text-muted-foreground">{resolved}</span>
      )}
    </>
  )
}

function OpenCycleRows({
  cycles,
  showMark,
  cols,
  kind,
}: {
  cycles: CryptoJournalOpenCycle[]
  showMark: boolean
  cols: number
  kind: 'crypto' | 'kis'
}) {
  if (cycles.length === 0) {
    return (
      <Table.Row>
        <Table.Cell colSpan={cols} className="p-0">
          <EmptyState title="보유 중인 포지션 없음." />
        </Table.Cell>
      </Table.Row>
    )
  }
  return (
    <>
      {cycles.map((c) => {
        const unrealised = c.unrealised_krw ? Number(c.unrealised_krw) : undefined
        return (
          <Table.Row key={c.symbol}>
            <Table.Cell>
              <SymbolCell sym={c.symbol} name={c.name} kind={kind} />
              {c.source === 'broker' && (
                <span
                  className="ml-1.5 text-[10px] uppercase tracking-wide text-muted-foreground border border-border rounded px-1 py-0.5"
                  title="체결 동기화 전 — 증권사 잔고에서 가져옴"
                >
                  잔고
                </span>
              )}
            </Table.Cell>
            <Table.Cell>{fmtVol(c.open_volume)}</Table.Cell>
            <Table.Cell>{fmtKRW(c.entry_avg)}</Table.Cell>
            {showMark && (
              <Table.Cell>{c.mark ? fmtKRW(c.mark) : '—'}</Table.Cell>
            )}
            <Table.Cell>{fmtKRW(c.entry_krw)}</Table.Cell>
            {showMark && (
              <Table.Cell className={pnlClass(unrealised)}>
                {c.unrealised_krw ? fmtSignedKRW(c.unrealised_krw) : '—'}
              </Table.Cell>
            )}
            {showMark && (
              <Table.Cell className={pnlClass(c.unrealised_pct)}>
                {fmtPct(c.unrealised_pct)}
              </Table.Cell>
            )}
            <Table.Cell>{c.bid_fills}</Table.Cell>
            <Table.Cell className="text-xs text-muted-foreground">
              {c.opened_at > 0 ? formatTime(c.opened_at) : '—'}
            </Table.Cell>
          </Table.Row>
        )
      })}
    </>
  )
}

function OpenCyclesSection({
  cycles,
  isPending,
  kind,
}: {
  cycles: CryptoJournalOpenCycle[] | undefined
  isPending: boolean
  kind: 'crypto' | 'kis'
}) {
  const showMark = kind === 'crypto'
  const cols = showMark ? 9 : 7

  const realPositions = (cycles ?? []).filter((c) => !c.is_dust)
  const dustPositions = (cycles ?? []).filter((c) => c.is_dust)

  const tableHead = (
    <Table.Head>
      <Table.Row>
        <Table.Header>{kind === 'crypto' ? '심볼' : '종목'}</Table.Header>
        <Table.Header>{kind === 'crypto' ? '수량' : '주식수'}</Table.Header>
        <Table.Header>진입 평균가</Table.Header>
        {showMark && <Table.Header>현재가</Table.Header>}
        <Table.Header>원가 (KRW)</Table.Header>
        {showMark && <Table.Header>미실현 PnL</Table.Header>}
        {showMark && <Table.Header>수익률</Table.Header>}
        <Table.Header>매수 횟수</Table.Header>
        <Table.Header>진입 시각</Table.Header>
      </Table.Row>
    </Table.Head>
  )

  return (
    <section>
      <h2 className="text-sm font-semibold text-muted-foreground mb-3">
        보유 중 (미실현)
      </h2>
      <Card className="overflow-x-auto">
        <Table>
          {tableHead}
          <Table.Body>
            {isPending ? (
              <SkeletonRows cols={cols} />
            ) : (
              <OpenCycleRows
                cycles={realPositions}
                showMark={showMark}
                cols={cols}
                kind={kind}
              />
            )}
          </Table.Body>
        </Table>
      </Card>

      {!isPending && dustPositions.length > 0 && (
        <div className="mt-4">
          <h3 className="text-xs font-medium text-muted-foreground mb-2">
            청산불가 dust (빗썸 5,000원 최소 미달)
          </h3>
          <Card className="overflow-x-auto opacity-60">
            <Table>
              {tableHead}
              <Table.Body>
                <OpenCycleRows
                  cycles={dustPositions}
                  showMark={showMark}
                  cols={cols}
                  kind={kind}
                />
              </Table.Body>
            </Table>
          </Card>
        </div>
      )}
    </section>
  )
}

function ClosedCyclesSection({
  cycles,
  isPending,
  kind,
}: {
  cycles: CryptoJournalClosedCycle[] | undefined
  isPending: boolean
  kind: 'crypto' | 'kis'
}) {
  return (
    <section>
      <h2 className="text-sm font-semibold text-muted-foreground mb-3">
        마감된 거래 (round-trip)
      </h2>
      <Card className="overflow-x-auto">
        <Table>
          <Table.Head>
            <Table.Row>
              <Table.Header>{kind === 'crypto' ? '심볼' : '종목'}</Table.Header>
              <Table.Header>진입 평균가</Table.Header>
              <Table.Header>청산 평균가</Table.Header>
              <Table.Header>{kind === 'crypto' ? '수량' : '주식수'}</Table.Header>
              <Table.Header>총 매수 (KRW)</Table.Header>
              <Table.Header>총 매도 (KRW)</Table.Header>
              <Table.Header>실현 PnL</Table.Header>
              <Table.Header>수익률</Table.Header>
              <Table.Header>매수/매도</Table.Header>
              <Table.Header>진입</Table.Header>
              <Table.Header>청산</Table.Header>
            </Table.Row>
          </Table.Head>
          <Table.Body>
            {isPending ? (
              <SkeletonRows cols={11} />
            ) : !cycles || cycles.length === 0 ? (
              <Table.Row>
                <Table.Cell colSpan={11} className="p-0">
                  <EmptyState title="아직 마감된 거래가 없습니다." />
                </Table.Cell>
              </Table.Row>
            ) : (
              cycles.map((c, idx) => {
                const realised = Number(c.realised_krw)
                return (
                  <Table.Row key={`${c.symbol}-${c.closed_at}-${idx}`}>
                    <Table.Cell>
                      <SymbolCell sym={c.symbol} name={c.name} kind={kind} />
                    </Table.Cell>
                    <Table.Cell>{fmtKRW(c.entry_avg)}</Table.Cell>
                    <Table.Cell>{fmtKRW(c.exit_avg)}</Table.Cell>
                    <Table.Cell>{fmtVol(c.entry_volume)}</Table.Cell>
                    <Table.Cell>{fmtKRW(c.entry_krw)}</Table.Cell>
                    <Table.Cell>{fmtKRW(c.exit_krw)}</Table.Cell>
                    <Table.Cell className={pnlClass(realised)}>
                      {fmtSignedKRW(c.realised_krw)}
                    </Table.Cell>
                    <Table.Cell className={pnlClass(c.pnl_pct)}>
                      {fmtPct(c.pnl_pct)}
                    </Table.Cell>
                    <Table.Cell className="text-xs text-muted-foreground">
                      {c.bid_fills}/{c.ask_fills}
                    </Table.Cell>
                    <Table.Cell className="text-xs text-muted-foreground">
                      {formatTime(c.opened_at)}
                    </Table.Cell>
                    <Table.Cell className="text-xs text-muted-foreground">
                      {formatTime(c.closed_at)}
                    </Table.Cell>
                  </Table.Row>
                )
              })
            )}
          </Table.Body>
        </Table>
      </Card>
    </section>
  )
}

function SkeletonRows({ cols }: { cols: number }) {
  return (
    <>
      {Array.from({ length: 3 }).map((_, i) => (
        <Table.Row key={i}>
          <Table.Cell colSpan={cols}>
            <span className="inline-block h-4 w-full rounded bg-muted animate-pulse" />
          </Table.Cell>
        </Table.Row>
      ))}
    </>
  )
}
